/**
 * Shared Hugeicons for admin feature/workspace chrome (#735 / #772).
 * Import from here — not lucide-react — in feature components and shell components.
 */
import {
  Activity01Icon,
  Alert02Icon,
  AlertCircleIcon,
  Cancel01Icon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  ChevronDownIcon as HugeChevronDownIcon,
  ChevronRightIcon as HugeChevronRightIcon,
  ClipboardCheckIcon as HugeClipboardCheckIcon,
  Clock01Icon,
  ExternalLinkIcon as HugeExternalLinkIcon,
  FileIcon,
  FlagIcon as HugeFlagIcon,
  Heading01Icon,
  HistoryIcon as HugeHistoryIcon,
  InformationCircleIcon,
  LayoutGridIcon as HugeLayoutGridIcon,
  LeftToRightBlockQuoteIcon,
  LeftToRightListBulletIcon,
  LeftToRightListNumberIcon,
  Link01Icon,
  LiveStreaming02Icon,
  Logout03Icon,
  MegaphoneIcon as HugeMegaphoneIcon,
  Message02Icon,
  MoonIcon as HugeMoonIcon,
  MusicThreeIcon,
  PanelLeftIcon as HugePanelLeftIcon,
  Redo02Icon,
  RefreshIcon,
  SecurityCheckIcon,
  Settings02Icon,
  SparklesIcon as HugeSparklesIcon,
  SunIcon as HugeSunIcon,
  TagIcon as HugeTagIcon,
  BadgeIcon as HugeBadgeIcon,
  UserSearchIcon as HugeUserSearchIcon,
  TextBoldIcon as HugeTextBoldIcon,
  TextItalicIcon as HugeTextItalicIcon,
  ToggleOffIcon,
  Undo02Icon,
  UserMultiple02Icon,
  Wrench01Icon,
} from '@hugeicons/core-free-icons'
import {
  HugeiconsIcon,
  type HugeiconsProps,
  type IconSvgElement,
} from '@hugeicons/react'
import {
  forwardRef,
  type ForwardRefExoticComponent,
  type RefAttributes,
  type SVGProps,
} from 'react'

type IconProps = SVGProps<SVGSVGElement> &
  Omit<HugeiconsProps, keyof SVGProps<SVGSVGElement> | 'altIcon' | 'icon'>

export type IconComponent = ForwardRefExoticComponent<
  IconProps & RefAttributes<SVGSVGElement>
>

function createIcon(icon: IconSvgElement, displayName: string): IconComponent {
  const Component = forwardRef<SVGSVGElement, IconProps>(
    ({ strokeWidth = 2, ...props }, ref) => (
      <HugeiconsIcon
        ref={ref}
        icon={icon}
        strokeWidth={
          typeof strokeWidth === 'number'
            ? strokeWidth
            : Number(strokeWidth) || 2
        }
        {...props}
      />
    ),
  )
  Component.displayName = displayName
  return Component
}

export const ActivityIcon = createIcon(Activity01Icon, 'ActivityIcon')
export const AlertTriangleIcon = createIcon(Alert02Icon, 'AlertTriangleIcon')
export const CheckCircle2Icon = createIcon(
  CheckmarkCircle02Icon,
  'CheckCircle2Icon',
)
export const ChevronDownIcon = createIcon(
  HugeChevronDownIcon,
  'ChevronDownIcon',
)
export const ChevronRightIcon = createIcon(
  HugeChevronRightIcon,
  'ChevronRightIcon',
)
export const CircleAlertIcon = createIcon(AlertCircleIcon, 'CircleAlertIcon')
export const ClipboardListIcon = createIcon(
  HugeClipboardCheckIcon,
  'ClipboardListIcon',
)
export const ClockIcon = createIcon(Clock01Icon, 'ClockIcon')
export const ExternalLinkIcon = createIcon(
  HugeExternalLinkIcon,
  'ExternalLinkIcon',
)
export const FileMusicIcon = createIcon(MusicThreeIcon, 'FileMusicIcon')
export const FileTextIcon = createIcon(FileIcon, 'FileTextIcon')
export const FlagIcon = createIcon(HugeFlagIcon, 'FlagIcon')
export const HelpCircleIcon = createIcon(
  InformationCircleIcon,
  'HelpCircleIcon',
)
/** Alias matching product web (`InfoIcon`) for banner/severity chrome. */
export const InfoIcon = createIcon(InformationCircleIcon, 'InfoIcon')
export const HistoryIcon = createIcon(HugeHistoryIcon, 'HistoryIcon')
export const LayoutDashboardIcon = createIcon(
  HugeLayoutGridIcon,
  'LayoutDashboardIcon',
)
export const LogOutIcon = createIcon(Logout03Icon, 'LogOutIcon')
export const MegaphoneIcon = createIcon(HugeMegaphoneIcon, 'MegaphoneIcon')
export const MessageSquareIcon = createIcon(Message02Icon, 'MessageSquareIcon')
export const MoonIcon = createIcon(HugeMoonIcon, 'MoonIcon')
export const PanelLeftIcon = createIcon(HugePanelLeftIcon, 'PanelLeftIcon')
export const RadioIcon = createIcon(LiveStreaming02Icon, 'RadioIcon')
export const RefreshCwIcon = createIcon(RefreshIcon, 'RefreshCwIcon')
export const Settings2Icon = createIcon(Settings02Icon, 'Settings2Icon')
export const ShieldAlertIcon = createIcon(Alert02Icon, 'ShieldAlertIcon')
export const ShieldIcon = createIcon(SecurityCheckIcon, 'ShieldIcon')
export const SparklesIcon = createIcon(HugeSparklesIcon, 'SparklesIcon')
export const SunIcon = createIcon(HugeSunIcon, 'SunIcon')
export const ToggleLeftIcon = createIcon(ToggleOffIcon, 'ToggleLeftIcon')
export const UsersIcon = createIcon(UserMultiple02Icon, 'UsersIcon')
export const TagIcon = createIcon(HugeTagIcon, 'TagIcon')
export const BadgeCopyIcon = createIcon(HugeBadgeIcon, 'BadgeCopyIcon')
export const UserSearchIcon = createIcon(HugeUserSearchIcon, 'UserSearchIcon')
export const WrenchIcon = createIcon(Wrench01Icon, 'WrenchIcon')
export const XCircleIcon = createIcon(CancelCircleIcon, 'XCircleIcon')
export const XIcon = createIcon(Cancel01Icon, 'XIcon')
export const TextBoldIcon = createIcon(HugeTextBoldIcon, 'TextBoldIcon')
export const TextItalicIcon = createIcon(HugeTextItalicIcon, 'TextItalicIcon')
export const LinkIcon = createIcon(Link01Icon, 'LinkIcon')
export const ListBulletIcon = createIcon(
  LeftToRightListBulletIcon,
  'ListBulletIcon',
)
export const ListOrderedIcon = createIcon(
  LeftToRightListNumberIcon,
  'ListOrderedIcon',
)
export const QuoteIcon = createIcon(LeftToRightBlockQuoteIcon, 'QuoteIcon')
export const UndoIcon = createIcon(Undo02Icon, 'UndoIcon')
export const RedoIcon = createIcon(Redo02Icon, 'RedoIcon')
export const HeadingIcon = createIcon(Heading01Icon, 'HeadingIcon')
